/**
 * 登入後：顯示此用戶所有商家（Square）列表，最下方為「新建商家」
 * 可從 URL ?token=xxx 取得 token（LINE 登入 callback 導回時）
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  listMySquares,
  switchSquareForJwt,
  type SquareInfo,
} from '../../api/liteSquaresClient';
import { getLiteJwtToken, setLiteJwtToken } from '../../api/liteAuthToken';
import { ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';

export function SquareChoicePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [hasToken, setHasToken] = useState<boolean | null>(null);
  const [squares, setSquares] = useState<SquareInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [enteringSuffix, setEnteringSuffix] = useState<string | null>(null);

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      try {
        setLiteJwtToken(tokenFromUrl);
      } catch (_) {
        // localStorage 不可用時略過（如私密模式）
      }
      const next = new URLSearchParams(searchParams);
      next.delete('token');
      setSearchParams(next, { replace: true });
    }
    const token = getLiteJwtToken();
    setHasToken(!!token);
    if (!token) {
      navigate('/lite/new-square', { replace: true });
    }
  }, [navigate, searchParams, setSearchParams]);

  // 已登入時載入此用戶所有商家
  useEffect(() => {
    if (!hasToken) return;

    const token = getLiteJwtToken();
    if (!token) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    listMySquares(token)
      .then(list => {
        if (!cancelled) setSquares(list);
      })
      .catch(() => {
        if (!cancelled) setSquares([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hasToken]);

  if (hasToken === null) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <p className="text-slate-600">載入中...</p>
      </div>
    );
  }

  if (!hasToken) return null;

  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-sm mx-auto w-full">
        <h1 className="text-xl font-semibold text-slate-800 text-center mb-1 mt-4">
          商家一覽
        </h1>
        <p className="text-sm text-slate-500 text-center mb-4">
          選擇商家進入，或於下方新建
        </p>

        {/* 區塊一：商家列表 */}
        <div className="rounded-xl bg-white border border-slate-200 shadow-sm p-3 space-y-2">
          {loading ? (
            <p className="text-center py-4 text-slate-500 text-sm">載入中...</p>
          ) : squares.length === 0 ? (
            <p className="text-center py-4 text-slate-500 text-sm">
              尚無商家，請於下方新建
            </p>
          ) : (
            squares.map(sq => (
              <button
                key={sq.id}
                type="button"
                disabled={enteringSuffix !== null}
                onClick={async () => {
                  const token = getLiteJwtToken();
                  if (!token) {
                    navigate('/lite/new-square', { replace: true });
                    return;
                  }
                  setEnteringSuffix(sq.suffix);
                  try {
                    const newToken = await switchSquareForJwt(token, sq.suffix);
                    try {
                      setLiteJwtToken(newToken);
                    } catch (_) {
                      // localStorage 不可用時略過
                    }
                    navigate(`/lite/shop/${sq.suffix}`);
                  } catch (err) {
                    toast.error(
                      err instanceof Error ? err.message : '無法進入該商家'
                    );
                  } finally {
                    setEnteringSuffix(null);
                  }
                }}
                className="w-full rounded-lg border border-slate-100 p-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition text-left disabled:opacity-50"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-800 truncate">
                    {sq.name || '（未命名）'}
                  </p>
                  {sq.type && (
                    <p className="text-sm text-slate-500 truncate">{sq.type}</p>
                  )}
                </div>
                <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />
              </button>
            ))
          )}
        </div>

        {/* 區塊二：新建商家 */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => navigate('/lite/square-create')}
            className="w-full rounded-lg bg-indigo-600 text-white font-medium py-3 hover:bg-indigo-700 transition"
          >
            新建商家
          </button>
        </div>
      </div>
    </div>
  );
}
